import { clamp01 } from './stringUtils';

const BASE_HEIGHT = 7.25;

function smoothStep(edge0: number, edge1: number, value: number) {
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function gaussian2D(x: number, z: number, centerX: number, centerZ: number, radiusX: number, radiusZ: number) {
  const normalizedX = (x - centerX) / radiusX;
  const normalizedZ = (z - centerZ) / radiusZ;
  return Math.exp(-(normalizedX * normalizedX + normalizedZ * normalizedZ));
}

function ridgeBand(distance: number, width: number) {
  const normalized = Math.abs(distance) / width;
  return Math.exp(-(normalized * normalized) * 1.6);
}

function routeMaskAt(x: number, z: number, centerline: (x: number) => number, width: number) {
  const delta = (z - centerline(x)) / width;
  return Math.exp(-(delta * delta) * 1.55);
}

function terrainDetailNoise(x: number, z: number) {
  return (
    Math.sin(x * 0.17 + z * 0.11) * 0.32 +
    Math.sin(x * 0.07 - z * 0.19) * 0.23 +
    Math.sin((x - z) * 0.05) * 0.14 +
    Math.cos((x + z) * 0.033) * 0.1
  );
}

function terrainHeightComponentsAt(x: number, z: number) {
  const basin = gaussian2D(x, z, 0, 0, 170, 128);
  const westBowl = gaussian2D(x, z, -148, -48, 72, 60);
  const eastBowl = gaussian2D(x, z, 146, 42, 76, 58);
  const redBaseShelf = gaussian2D(x, z, -78, 0, 48, 42);
  const blueBaseShelf = gaussian2D(x, z, 78, 0, 48, 42);
  const redSpawnShelf = gaussian2D(x, z, -70, 0, 58, 48);
  const blueSpawnShelf = gaussian2D(x, z, 70, 0, 58, 48);
  const northRidge = ridgeBand(z - 138, 32) * (0.62 + 0.38 * gaussian2D(x, z, 0, 128, 240, 50));
  const southRidge = ridgeBand(z + 136, 33) * (0.64 + 0.36 * gaussian2D(x, z, 0, -122, 240, 52));
  const westSpine = ridgeBand(x + 190, 30) * (0.55 + 0.45 * gaussian2D(x, z, -184, 0, 52, 160));
  const eastSpine = ridgeBand(x - 194, 30) * (0.56 + 0.44 * gaussian2D(x, z, 186, 0, 54, 160));
  const saddleWest = gaussian2D(x, z, -54, 10, 56, 26);
  const saddleEast = gaussian2D(x, z, 60, -14, 60, 28);
  const centralSaddle = gaussian2D(x, z, 0, 0, 84, 32);
  const routeMain = routeMaskAt(x, z, terrainMainRouteCenterlineAt, 15.5);
  const routeSecondary = routeMaskAt(x, z, terrainSecondaryRouteCenterlineAt, 17.5);

  return {
    basin,
    westBowl,
    eastBowl,
    redBaseShelf,
    blueBaseShelf,
    redSpawnShelf,
    blueSpawnShelf,
    northRidge,
    southRidge,
    westSpine,
    eastSpine,
    saddleWest,
    saddleEast,
    centralSaddle,
    routeMain,
    routeSecondary,
  };
}

function terrainHeightAtInternal(x: number, z: number) {
  const components = terrainHeightComponentsAt(x, z);
  const baseShelves =
    components.redBaseShelf * 9.4 +
    components.blueBaseShelf * 9.4 +
    components.redSpawnShelf * 3.2 +
    components.blueSpawnShelf * 3.2;
  const base = BASE_HEIGHT + 1.6 * gaussian2D(x, z, 0, 0, 240, 180) + baseShelves;
  const ridge =
    components.northRidge * 10.8 +
    components.southRidge * 10.2 +
    components.westSpine * 7.4 +
    components.eastSpine * 7.0;
  const carve =
    components.basin * 7.4 +
    components.westBowl * 9.0 +
    components.eastBowl * 8.6 +
    components.centralSaddle * 3.8 +
    components.saddleWest * 3.0 +
    components.saddleEast * 2.8;
  const routes = components.routeMain * 4.8 + components.routeSecondary * 3.6;
  const detail = terrainDetailNoise(x, z) * (0.5 + 0.6 * Math.max(components.routeMain, components.routeSecondary) + 0.08 * ridge);

  let h = base + ridge - carve - routes + detail;

  // Deeper central valley channel: creates natural ski lane along map center (|x| < 20, |z| < 15)
  if (Math.abs(x) < 20 && Math.abs(z) < 15) {
    const t = clamp01((20 - Math.abs(x)) / 20);
    const ss = t * t * (3 - 2 * t);
    const zFade = clamp01(1 - Math.abs(z) / 15);
    h -= 6 * ss * zFade;
  }

  // 4 natural ski ramps: Gaussian bumps (sigma=8, bump_height=6)
  const rampSigma = 8;
  const rampSigma2 = 2 * rampSigma * rampSigma;
  const rampHeight = 6;
  const rampPositions: Array<[number, number]> = [[-30, -20], [30, -20], [-25, 15], [25, 15]];
  for (const [cx, cz] of rampPositions) {
    h += rampHeight * Math.exp(-((x - cx) * (x - cx) + (z - cz) * (z - cz)) / rampSigma2);
  }

  // Steeper flag base approaches: concave bowl near flag base positions
  const flagBases: Array<[number, number]> = [[-78, 0], [78, 0]];
  for (const [fx, fz] of flagBases) {
    const dist = Math.hypot(x - fx, z - fz);
    if (dist < 18) {
      h -= 3 * (1 - dist / 18) * (1 - dist / 18);
    }
  }

  return h;
}

export function terrainMainRouteCenterlineAt(x: number) {
  return 74 * Math.sin(x * 0.013 + 0.35) + 8 * Math.sin(x * 0.047);
}

export function terrainSecondaryRouteCenterlineAt(x: number) {
  return -60 * Math.sin(x * 0.0142 - 0.72) - 26 + 5 * Math.cos(x * 0.021);
}

export function terrainMainRouteCenterlineAtDangerousCrossing(x: number) {
  return 22 * Math.sin(x * 0.018);
}

export function terrainSecondaryRouteCenterlineAtDangerousCrossing(x: number) {
  return -4 + 28 * Math.sin(x * 0.014 + 0.18);
}

export function terrainMainRouteCenterlineAtMap(mapId: string, x: number) {
  if (mapId === 'dangerouscrossing') return terrainMainRouteCenterlineAtDangerousCrossing(x);
  if (mapId === 'raindance') return terrainMainRouteCenterlineAtRaindance(x);
  return terrainMainRouteCenterlineAt(x);
}

export function terrainSecondaryRouteCenterlineAtMap(mapId: string, x: number) {
  if (mapId === 'dangerouscrossing') return terrainSecondaryRouteCenterlineAtDangerousCrossing(x);
  if (mapId === 'raindance') return terrainSecondaryRouteCenterlineAtRaindance(x);
  return terrainSecondaryRouteCenterlineAt(x);
}

export type TerrainSurfaceSample = {
  height: number;
  slope: number;
  slopeX: number;
  slopeZ: number;
  bowl: number;
  ridge: number;
  saddle: number;
  routeMain: number;
  routeSecondary: number;
  route: number;
  exposure: number;
  snow: number;
  ice: number;
  rock: number;
};

export function terrainHeightAt(x: number, z: number): number {
  return terrainHeightAtInternal(x, z);
}

export function terrainHeightAtRaindance(x: number, z: number): number {
  const base = Math.sin(x * 0.03) * Math.cos(z * 0.03) * 4;
  const ravine = Math.exp(-(x * x) / 800) * (-12);
  const cliffs = (Math.tanh((Math.abs(x) - 25) * 0.15) * 0.5 + 0.5) * 18;
  const bridge = Math.exp(-(z * z) / 200) * Math.exp(-(x * x) / 400) * 8;
  const noise =
    Math.sin(x * 0.12 + z * 0.08) * 1.5 +
    Math.cos(x * 0.07) * Math.sin(z * 0.11) * 2;

  let h = base + ravine + cliffs + bridge + noise;

  // River valley: central strip |z| < 15, only in center (|x| < 30) to preserve cliff heights
  const absZ = Math.abs(z);
  const absX = Math.abs(x);
  if (absZ < 15 && absX < 30) {
    h -= 5 * Math.max(0, 1 - absZ / 15) * Math.max(0, 1 - absX / 30);
  }

  // East vs west height asymmetry: x>0 side is 4 units higher on average
  h += Math.max(0, x * 0.05);

  // Dam approach: near position (0,-40) to (0,-55), elevated platform
  const absZ2 = Math.abs(z);
  h += 8 * smoothStep(45, 55, absZ2) * Math.max(0, 1 - absX / 20);

  return h;
}

function terrainDetailNoiseRD(x: number, z: number) {
  return (
    Math.sin(x * 0.14 + z * 0.09) * 0.36 +
    Math.sin(x * 0.06 - z * 0.16) * 0.26 +
    Math.sin((x - z) * 0.045) * 0.15 +
    Math.cos((x + z) * 0.032) * 0.11
  );
}

export function terrainMainRouteCenterlineAtRaindance(x: number) {
  return 16 * Math.sin(x * 0.016);
}

export function terrainSecondaryRouteCenterlineAtRaindance(x: number) {
  return -10 + 24 * Math.sin(x * 0.012 + 0.4);
}

export function terrainHeightAtDangerousCrossing(x: number, z: number): number {
  const DC_BASE_HEIGHT = 9.5;

  const redBaseShelf = gaussian2D(x, z, -120, 0, 52, 46);
  const blueBaseShelf = gaussian2D(x, z, 120, 0, 52, 46);
  const redSpawnShelf = gaussian2D(x, z, -112, 0, 62, 50);
  const blueSpawnShelf = gaussian2D(x, z, 112, 0, 62, 50);

  const centralCanyon = gaussian2D(x, z, 0, 0, 90, 34) * 12.5;
  const canyonDeep = gaussian2D(x, z, 0, 0, 60, 18) * 8.0;

  const westValley = gaussian2D(x, z, -80, -30, 55, 48) * 6.5;
  const eastValley = gaussian2D(x, z, 80, 30, 55, 48) * 6.5;

  const northRidge = ridgeBand(z - 130, 26) * (0.72 + 0.38 * gaussian2D(x, z, 0, 120, 260, 45));
  const southRidge = ridgeBand(z + 128, 28) * (0.7 + 0.4 * gaussian2D(x, z, 0, -115, 260, 48));
  const westSpine = ridgeBand(x + 230, 34) * (0.6 + 0.5 * gaussian2D(x, z, -220, 0, 56, 170));
  const eastSpine = ridgeBand(x - 234, 34) * (0.58 + 0.52 * gaussian2D(x, z, 224, 0, 58, 170));

  const canyonRidge = ridgeBand(z, 22) * gaussian2D(x, z, 0, 0, 140, 50) * 4.5;

  const base =
    DC_BASE_HEIGHT +
    redBaseShelf * 11.0 +
    blueBaseShelf * 11.0 +
    redSpawnShelf * 3.8 +
    blueSpawnShelf * 3.8 +
    2.2 * gaussian2D(x, z, 0, 0, 280, 200);

  const ridge =
    northRidge * 13.5 +
    southRidge * 13.0 +
    westSpine * 9.0 +
    eastSpine * 8.6 +
    canyonRidge;

  const carve = centralCanyon + canyonDeep + westValley + eastValley;

  const detail = terrainDetailNoiseDC(x, z) * 0.6;

  let h = base + ridge - carve + detail;

  // Canyon wall effect: near map edges |x| > 55, add cliff walls
  const absX = Math.abs(x);
  if (absX > 55) {
    h += Math.max(0, (absX - 55) * 4.5);
  }

  // Sand-dune ridge ramps (sigma=12, bump_height=5) at (-35,0), (0,15), (40,-10)
  const duneSigma = 12;
  const duneSigma2 = 2 * duneSigma * duneSigma;
  const duneHeight = 5;
  const dunePositions: Array<[number, number]> = [[-35, 0], [0, 15], [40, -10]];
  for (const [cx, cz] of dunePositions) {
    h += duneHeight * Math.exp(-((x - cx) * (x - cx) + (z - cz) * (z - cz)) / duneSigma2);
  }

  // Increase overall height variation by 15% (non-base heights scaled up)
  // base height is DC_BASE_HEIGHT; scale the delta above base by 1.15
  h = DC_BASE_HEIGHT + (h - DC_BASE_HEIGHT) * 1.15;

  return h;
}

function terrainDetailNoiseDC(x: number, z: number) {
  return (
    Math.sin(x * 0.13 + z * 0.09) * 0.38 +
    Math.sin(x * 0.06 - z * 0.15) * 0.28 +
    Math.sin((x - z) * 0.04) * 0.16 +
    Math.cos((x + z) * 0.028) * 0.12
  );
}

export function terrainHeightAtMap(mapId: string, x: number, z: number): number {
  if (mapId === 'dangerouscrossing') {
    return terrainHeightAtDangerousCrossing(x, z);
  }
  if (mapId === 'raindance') {
    return terrainHeightAtRaindance(x, z);
  }
  return terrainHeightAt(x, z);
}

export function terrainSurfaceAt(x: number, z: number): TerrainSurfaceSample {
  const height = terrainHeightAtInternal(x, z);
  const slopeX = terrainHeightAtInternal(x + 2, z) - terrainHeightAtInternal(x - 2, z);
  const slopeZ = terrainHeightAtInternal(x, z + 2) - terrainHeightAtInternal(x, z - 2);
  const slope = clamp01(Math.hypot(slopeX, slopeZ) / 9.5);
  const components = terrainHeightComponentsAt(x, z);
  const bowl = clamp01((components.basin * 0.9 + components.westBowl + components.eastBowl) / 2.7);
  const ridge = clamp01((components.northRidge + components.southRidge + components.westSpine + components.eastSpine) / 2.6);
  const saddle = clamp01((components.centralSaddle + components.saddleWest + components.saddleEast + components.routeMain * 0.8 + components.routeSecondary * 0.45) / 2.7);
  const routeMain = clamp01(components.routeMain);
  const routeSecondary = clamp01(components.routeSecondary);
  const route = clamp01(routeMain * 0.92 + routeSecondary * 0.74);
  const heightBand = smoothStep(2.5, 21, height);
  const exposure = clamp01(ridge * 0.55 + heightBand * 0.28 + slope * 0.34 - bowl * 0.18);
  const northFacing = clamp01(-slopeZ / 7.0);
  const snowLine = smoothStep(6, 24, height);
  const snow = clamp01(0.52 + heightBand * 0.4 - slope * 0.6 - route * 0.35 + bowl * 0.14 + northFacing * 0.06 + snowLine * 0.08);
  const ice = clamp01(route * 0.88 + slope * 0.22 + exposure * 0.18 - snow * 0.25);
  const rock = clamp01(slope * 0.88 + ridge * 0.52 + exposure * 0.18 - snow * 0.45 - route * 0.1);
  return {
    height,
    slope,
    slopeX,
    slopeZ,
    bowl,
    ridge,
    saddle,
    routeMain,
    routeSecondary,
    route,
    exposure,
    snow,
    ice,
    rock,
  };
}

function terrainSurfaceAtRaindance(x: number, z: number): TerrainSurfaceSample {
  const height = terrainHeightAtRaindance(x, z);
  const slopeX = terrainHeightAtRaindance(x + 2, z) - terrainHeightAtRaindance(x - 2, z);
  const slopeZ = terrainHeightAtRaindance(x, z + 2) - terrainHeightAtRaindance(x, z - 2);
  const slope = clamp01(Math.hypot(slopeX, slopeZ) / 10.5);
  const ravine = gaussian2D(x, z, 0, 0, 90, 38);
  const cliffWest = ridgeBand(x + 95, 28);
  const cliffEast = ridgeBand(x - 95, 28);
  const routeMain = clamp01(routeMaskAt(x, z, terrainMainRouteCenterlineAtRaindance, 18));
  const routeSecondary = clamp01(routeMaskAt(x, z, terrainSecondaryRouteCenterlineAtRaindance, 20));
  const route = clamp01(routeMain * 0.88 + routeSecondary * 0.78);
  const heightBand = smoothStep(-8, 24, height);
  const bowl = clamp01(ravine * 0.9);
  const ridge = clamp01((cliffWest + cliffEast) / 1.8);
  const saddle = clamp01(route * 0.7 + gaussian2D(x, z, 0, 0, 120, 48) * 0.45);
  const exposure = clamp01(ridge * 0.62 + heightBand * 0.34 + slope * 0.38 - bowl * 0.16);
  const wetness = smoothStep(-6, -1, height) * (1 - smoothStep(0, 4, height));
  const snow = clamp01(0.05 + heightBand * 0.12 - slope * 0.4 - route * 0.2 + bowl * 0.04 - wetness * 0.15);
  const ice = clamp01(route * 0.7 + bowl * 0.15 + slope * 0.15 + exposure * 0.1 - snow * 0.2 + wetness * 0.12);
  const rock = clamp01(slope * 0.92 + ridge * 0.58 + exposure * 0.22 - snow * 0.2 - route * 0.08 + bowl * 0.3);
  return {
    height,
    slope,
    slopeX,
    slopeZ,
    bowl,
    ridge,
    saddle,
    routeMain,
    routeSecondary,
    route,
    exposure,
    snow,
    ice,
    rock,
  };
}

function terrainSurfaceAtDangerousCrossing(x: number, z: number): TerrainSurfaceSample {
  const height = terrainHeightAtDangerousCrossing(x, z);
  const slopeX = terrainHeightAtDangerousCrossing(x + 2, z) - terrainHeightAtDangerousCrossing(x - 2, z);
  const slopeZ = terrainHeightAtDangerousCrossing(x, z + 2) - terrainHeightAtDangerousCrossing(x, z - 2);
  const slope = clamp01(Math.hypot(slopeX, slopeZ) / 10.5);
  const canyon = gaussian2D(x, z, 0, 0, 112, 44);
  const rimNorth = ridgeBand(z - 130, 26);
  const rimSouth = ridgeBand(z + 128, 28);
  const spineWest = ridgeBand(x + 230, 34);
  const spineEast = ridgeBand(x - 234, 34);
  const routeMain = clamp01(routeMaskAt(x, z, terrainMainRouteCenterlineAtDangerousCrossing, 18));
  const routeSecondary = clamp01(routeMaskAt(x, z, terrainSecondaryRouteCenterlineAtDangerousCrossing, 20));
  const route = clamp01(routeMain * 0.88 + routeSecondary * 0.78);
  const heightBand = smoothStep(-6, 28, height);
  const bowl = clamp01(canyon * 0.9 + gaussian2D(x, z, -80, -30, 58, 50) * 0.36 + gaussian2D(x, z, 80, 30, 58, 50) * 0.36);
  const ridge = clamp01((rimNorth + rimSouth + spineWest + spineEast) / 2.9);
  const saddle = clamp01(route * 0.7 + gaussian2D(x, z, 0, 0, 148, 54) * 0.45);
  const exposure = clamp01(ridge * 0.62 + heightBand * 0.34 + slope * 0.38 - bowl * 0.16);
  const northFacing = clamp01(-slopeZ / 7.0);
  const snow = clamp01(0.48 + heightBand * 0.42 - slope * 0.58 - route * 0.28 + bowl * 0.08 + northFacing * 0.05);
  const ice = clamp01(route * 0.86 + bowl * 0.2 + slope * 0.2 + exposure * 0.14 - snow * 0.2);
  const rock = clamp01(slope * 0.92 + ridge * 0.58 + exposure * 0.22 - snow * 0.38 - route * 0.08);

  return {
    height,
    slope,
    slopeX,
    slopeZ,
    bowl,
    ridge,
    saddle,
    routeMain,
    routeSecondary,
    route,
    exposure,
    snow,
    ice,
    rock,
  };
}

export function terrainSurfaceAtMap(mapId: string, x: number, z: number): TerrainSurfaceSample {
  if (mapId === 'dangerouscrossing') {
    return terrainSurfaceAtDangerousCrossing(x, z);
  }
  if (mapId === 'raindance') {
    return terrainSurfaceAtRaindance(x, z);
  }
  return terrainSurfaceAt(x, z);
}

export function isInWaterZone(x: number, z: number, y: number, mapId: string): boolean {
  if (mapId !== 'raindance') return false;
  if (y >= -5) return false;
  if (Math.abs(x) > 15) return false;
  if (Math.abs(z) > 100) return false;
  return true;
}

export function sampleHeight(x: number, z: number, mapId: string, precision: 'normal' | 'fine' = 'normal'): number {
  // precision 'fine' uses 0.25u interpolation step for better physics accuracy on ski ramps
  if (precision === 'fine') {
    // bilinear interpolation at 0.25u step
    const step = 0.25;
    const x0 = Math.floor(x / step) * step;
    const z0 = Math.floor(z / step) * step;
    const tx = (x - x0) / step;
    const tz = (z - z0) / step;
    const h00 = terrainHeightAtMap(mapId, x0, z0);
    const h10 = terrainHeightAtMap(mapId, x0 + step, z0);
    const h01 = terrainHeightAtMap(mapId, x0, z0 + step);
    const h11 = terrainHeightAtMap(mapId, x0 + step, z0 + step);
    return h00 * (1 - tx) * (1 - tz) + h10 * tx * (1 - tz) + h01 * (1 - tx) * tz + h11 * tx * tz;
  }
  return terrainHeightAtMap(mapId, x, z);
}

export function getSurfaceType(x: number, z: number, mapId: string): 'snow' | 'rock' | 'sand' | 'metal' | 'water' {
  if (mapId === 'katabatic') return 'snow';
  if (mapId === 'dangerouscrossing') return 'sand';
  if (mapId === 'raindance') {
    const h = sampleHeight(x, z, mapId);
    return h < 1.5 ? 'water' : 'rock';
  }
  return 'rock';
}
